'use client';

import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import type { ReactNode } from 'react';

import { LEGAL_FOOTNOTES_ID } from '@/app/consts/ids';

export type Tag =
  | 'break'
  | 'breakSm'
  | 'breakLg'
  | 'wbreak'
  | 'strong'
  | 'b'
  | 'em'
  | 'i'
  | 'u'
  | 'sup'
  | 'li'
  | 'sr'
  | 'p'
  | 'w'
  | 'uppercase'
  | 'Link'
  | 'ExternalLink'
  | 'EmailLink'
  | 'HomeLink'
  | 'TermsLink'
  | 'IpNoticeLink'
  | 'FootnoteLink'
  | 'ScienceResearchLink';

export interface LocaleStringTag {
  r: string;
  tm: string;
}

export const getLocaleFormatString: LocaleStringTag = {
  // In Phrase, these tokens are simple text replacements for values wrapped in curly braces like {tm}
  r: String.fromCharCode(174),
  tm: String.fromCharCode(8482),
};

const getLocaleFormatting: Record<Tag, (chunks: ReactNode) => ReactNode> = {
  // Simple HTML tags (i.e. <b>bold text</b>)
  break: function breakTag() {
    return <br />;
  },
  breakSm: function breakSm() {
    return <br className="block md:hidden" />;
  },
  breakLg: function breakLg() {
    return <br className="hidden lg:block" />;
  },
  wbreak: function wbreakTag() {
    return <wbr />;
  },
  strong: function strong(chunks: ReactNode) {
    return <strong>{chunks}</strong>;
  },
  b: function b(chunks: ReactNode) {
    return <strong>{chunks}</strong>;
  },
  em: function em(chunks: ReactNode) {
    return <em className="font-serif italic">{chunks}</em>;
  },
  i: function i(chunks: ReactNode) {
    return <em>{chunks}</em>;
  },
  u: function u(chunks: ReactNode) {
    return <span className="underline">{chunks}</span>;
  },
  sup: function sup(chunks: ReactNode) {
    return <sup>{chunks}</sup>;
  },
  li: function li(chunks: ReactNode) {
    return <li>{chunks}</li>;
  },
  sr: function sr(chunks: ReactNode) {
    return <span className="sr-only">{chunks}</span>;
  },
  p: function p(chunks: ReactNode) {
    return <Typography color="inherit">{chunks}</Typography>;
  },
  // Japanese-specific formatting for {w} characters
  w: function w(chunks: ReactNode) {
    return <>{chunks}</>;
  },
  uppercase: function uppercase(chunks: ReactNode) {
    return <span className="uppercase">{chunks}</span>;
  },

  // React Components & more complex HTML tag usage
  Link: function Link(href: ReactNode) {
    return <BodyLink href={href as string}>{href}</BodyLink>;
  },

  ExternalLink: function ExternalLink(href: any) {
    return (
      <BodyLink href={href[0] as string} target="_blank" rel="noreferrer">
        {href}
      </BodyLink>
    );
  },

  EmailLink: function EmailLink(email: ReactNode) {
    return <BodyLink href={`mailto:${email as string}`}>{email}</BodyLink>;
  },

  HomeLink: function HomeLink(chunks: ReactNode) {
    return (
      <BodyLink href="/" color="inherit">
        {chunks}
      </BodyLink>
    );
  },

  TermsLink: function TermsLink(text: ReactNode) {
    return <BodyLink href="/terms-and-conditions">{text}</BodyLink>;
  },

  IpNoticeLink: function IpNoticeLink(text: ReactNode) {
    return <BodyLink href="/intellectual-property-notice">{text}</BodyLink>;
  },

  FootnoteLink: (footnoteSymbol: ReactNode) => {
    return (
      <sup>
        <a href={`#${LEGAL_FOOTNOTES_ID}`}>{footnoteSymbol}</a>
      </sup>
    );
  },

  ScienceResearchLink: function ScienceResearchLink(text: ReactNode) {
    return <BodyLink href="/science-and-research">{text}</BodyLink>;
  },
};

export default getLocaleFormatting;
